import React, { useState, useRef,useEffect } from "react";
import { Tabs, Card, Typography, Input, Button, Spin } from "antd";
import { useAuth } from "../../../context/AuthContext";
import JoiningKit from "../components/JoiningKit";
import ESI from "../components/ESI";
import Compliance from "../components/Compliance";
import styled from "styled-components";
import jwt_decode from "jwt-decode";
import FormStatus from "../components/FormStatus";
import { trackFeatureAction } from "../../../analytics/googleAnalytics";
import { useDispatch, useSelector } from "react-redux";
import {
  SearchOutlined,
  ReloadOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  clearAllData,
  clearEmployeeData,
} from "../../../redux/slice/hrFormSlice";
import { fetchMasterData } from "../../../redux/slice/masterSlice";

const { Title, Text } = Typography;

const StyledTabs = styled(Tabs)`
  &.ant-tabs {
    font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
      "Helvetica Neue", Arial, sans-serif;
    font-size: 24px;
    font-weight: 600;
  }

  .ant-tabs-nav {
    margin: 0;
    padding: 0 24px;
    background: #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    border-radius: 8px 8px 0 0;

    &::before {
      border-bottom: 1px solid #f0f0f0;
    }
  }

  .ant-tabs-tab {
    padding: 16px 24px;
    font-size: 18px;
    font-weight: 500;
    color: #6b7280;
    transition: all 0.3s ease;

    &:hover {
      color: #4f46e5;
    }

    .ant-tabs-tab-btn {
      display: flex;
      align-items: center;
      gap: 8px;
    }
  }

  .ant-tabs-tab-active {
    .ant-tabs-tab-btn {
      color: #4f46e5;
      font-weight: 600;
    }
  }

  .ant-tabs-ink-bar {
    background: #4f46e5;
    height: 3px;
  }
`;

const TabContentContainer = styled.div`
  background: #fff;
  padding: 24px;
  border-radius: 0 0 8px 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  min-height: 60vh;
  overflow-y: auto;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 10px;

    &:hover {
      background: #9ca3af;
    }
  }
`;

const MainContainer = styled.div`
  padding: 24px;
  background: #f9fafb;
  min-height: 100vh;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const SearchContainer = styled.div`
  background: #fff;
  padding: 24px;
  margin-bottom: 16px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;

  .ant-input {
    max-width: 300px;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;

    .ant-input {
      width: 100%;
      max-width: 100%;
    }
  }
`;

const MainHrForm = (props) => {
  const { authState } = useAuth();
  const jwtBearer = authState.token;
  var jwt_payload = "";
  var jwt_payload_group = [];
  var user_id = "";
  var user_role = "";
  var user_name = "";
  var user_branch = "";

  if (jwtBearer) {
    jwt_payload = jwt_decode(jwtBearer);
    user_name = props.userDetails?.username;
    user_id = jwt_payload.user_id;
    user_role = props.userDetails?.role;
    jwt_payload_group = props.userDetails?.group_id;
    user_branch = props.userDetails?.branch_id;
  }

  // State for HR/admin view
  const [searchUserId, setSearchUserId] = useState("");
  const [activeUserId, setActiveUserId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTrigger, setSearchTrigger] = useState(0);
  const [isSearchDisabled, setIsSearchDisabled] = useState(false);

  const dispatch = useDispatch();

  const joiningKitRef = useRef();
  const esiRef = useRef();
  const complianceRef = useRef();

  const handleSearch = () => {
    if (searchUserId.trim() && searchUserId !== activeUserId) {
      trackFeatureAction("MainHrForm", "Forms Status", "search");
      setIsLoading(true);
      setTimeout(() => {
        setActiveUserId(searchUserId.toUpperCase());
        setSearchTrigger((prev) => prev + 1);
        setIsLoading(false);
      }, 500);
    }
    setIsSearchDisabled(true);
  };

  useEffect(() => {
    if (jwtBearer) {
      const fetchData = async () => {
        dispatch(fetchMasterData({ field: "esi_users", jwtBearer }));
      };

      fetchData();
    }
  }, [dispatch, jwtBearer]);

  const master_form_user_ids = useSelector(
    (state) => state?.masterSlice?.esi_users || []
  );
  // console.log("master_form_user_ids", master_form_user_ids);

  const handleReset = () => {
    trackFeatureAction("MainHrForm", "Forms Status", "reset");
    if (joiningKitRef.current) joiningKitRef.current.resetAllForms();
    if (esiRef.current) esiRef.current.resetAllForms();
    if (complianceRef.current) complianceRef.current.resetAllForms();

    setIsSearchDisabled(false);
    setSearchUserId("");
    dispatch(clearAllData());
    setSearchTrigger((prev) => prev + 1);

    setActiveUserId(null);
  };

  // console.log("jwt_payload_group", jwt_payload_group);

  // console.log("activeUserId", activeUserId);

  if (!(jwt_payload_group === "10" || jwt_payload_group === "14" || jwt_payload_group === "18") ) {
    return (
      <MainContainer>
        <StyledTabs
          defaultActiveKey="1"
          onChange={(key) => {
            const featureMap = {
              "1": "ESI Form",
              "2": "Compliance Form",
            };

            const feature = featureMap[key];

            if (feature) {
              trackFeatureAction(
                "MainHrForm",
                feature,
                "open"
              );
            }
          }}
        >
          {/* <Tabs.TabPane
            tab={<span style={{ display: 'flex', alignItems: 'center' }}>Joining Kit Form</span>}
            key="1"
          >
            <TabContentContainer>
              <JoiningKit user_id={user_id} jwt_payload_group={jwt_payload_group} ref={joiningKitRef}/>
            </TabContentContainer>
          </Tabs.TabPane> */}

         {master_form_user_ids.includes(user_id) && ( <Tabs.TabPane
            tab={
              <span style={{ display: "flex", alignItems: "center" }}>
                ESI Form
              </span>
            }
            key="1"
          >
            <TabContentContainer>
              <ESI
                user_id={user_id}
                jwt_payload_group={jwt_payload_group}
                ref={esiRef}
              />
            </TabContentContainer>
          </Tabs.TabPane>)}

          <Tabs.TabPane
            tab={
              <span style={{ display: "flex", alignItems: "center" }}>
                Compliance Form
              </span>
            }
            key="2"
          >
            <TabContentContainer>
              <Compliance
                user_id={user_id}
                jwt_payload_group={jwt_payload_group}
                ref={complianceRef}
              />
            </TabContentContainer>
          </Tabs.TabPane>
        </StyledTabs>
      </MainContainer>
    );
  } else if (jwt_payload_group === "10" || jwt_payload_group === "14" || jwt_payload_group === "18") {
    return (
      <MainContainer>
        <SearchContainer>
          <Input
            placeholder="Search Employee ID"
            value={searchUserId}
            onChange={(e) => setSearchUserId(e.target.value)}
            prefix={
              <SearchOutlined
                style={{
                  color: "rgba(0, 0, 0, 0.25)",
                  verticalAlign: "middle",
                  lineHeight: "40px",
                }}
              />
            }
            style={{
              width: 320,
              height: 40,
              borderRadius: 4,
              transition: "all 0.3s",
              display: "flex",
              alignItems: "center",
            }}
            allowClear
            onPressEnter={handleSearch}
            disabled={isSearchDisabled}
          />
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleSearch}
            disabled={!searchUserId.trim()}
            style={{
              height: 40,
              padding: "0 16px",
              fontWeight: 500,
              borderRadius: 4,
            }}
          >
            Search
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleReset}
            style={{
              height: 40,
              padding: "0 16px",
              borderRadius: 4,
              borderColor: "#d9d9d9",
            }}
          >
            Reset
          </Button>
        </SearchContainer>

        <Spin spinning={isLoading}>
          <StyledTabs
            defaultActiveKey="1"
            tabBarExtraContent={
              activeUserId && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginRight: "16px",
                    padding: "6px 12px",
                    background: "#f0f5ff",
                    borderRadius: "4px",
                    border: "1px solid #d6e4ff",
                    color: "#1d39c4",
                    fontWeight: 500,
                    fontSize: "16px",
                  }}
                >
                  <UserOutlined style={{ color: "#1d39c4" }} />
                  <span>Employee ID: {activeUserId}</span>
                </div>
              )
            }
          >
            <Tabs.TabPane
              tab={
                <span style={{ display: "flex", alignItems: "center" }}>
                  Status
                </span>
              }
              key="1"
            >
              <TabContentContainer>
                <FormStatus user_id={activeUserId} />
              </TabContentContainer>
            </Tabs.TabPane>

            {activeUserId !== null && (
              <>
                {/* <Tabs.TabPane
                    tab={<span style={{ display: 'flex', alignItems: 'center' }}>Joining Kit Form</span>}
                    key="2"
                  >
                    <TabContentContainer>
                      <JoiningKit user_id={activeUserId} jwt_payload_group={jwt_payload_group} ref={joiningKitRef} />
                    </TabContentContainer>
                  </Tabs.TabPane> */}

                <Tabs.TabPane
                  tab={
                    <span style={{ display: "flex", alignItems: "center" }}>
                      ESI Form
                    </span>
                  }
                  key="2"
                >
                  <TabContentContainer>
                    <ESI
                      user_id={activeUserId}
                      jwt_payload_group={jwt_payload_group}
                      ref={esiRef}
                    />
                  </TabContentContainer>
                </Tabs.TabPane>

                <Tabs.TabPane
                  tab={
                    <span style={{ display: "flex", alignItems: "center" }}>
                      Compliance Form
                    </span>
                  }
                  key="3"
                >
                  <TabContentContainer>
                    <Compliance
                      user_id={activeUserId}
                      jwt_payload_group={jwt_payload_group}
                      ref={complianceRef}
                    />
                  </TabContentContainer>
                </Tabs.TabPane>
              </>
            )}
          </StyledTabs>
        </Spin>
      </MainContainer>
    );
  }

  return null;
};

export default MainHrForm;
